<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('CMS-Konfiguration') }}</h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" to="/workspace/cms">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <loading :status="loading"/>

            <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                <app-panel :title="$t('Tailwind CSS')" panel-class="h-full xl:col-span-2" body-class="!p-0">
                        <div class="divide-y divide-bordergray-100">
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Status') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">
                                    <span class="text-sm font-medium" :class="statusClass">{{ statusLabel }}</span>
                                </div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Tailwind Version') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ status.tailwind_version || '-' }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Letzte CMS-Änderung') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ formatDateTime(status.last_changed_at) }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Letzte Kompilierung') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ formatDateTime(status.last_build_at) }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Aktive Templates') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ status.active_templates_count ?? 0 }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Aktive Seiten') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ status.active_pages_count ?? 0 }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Aktive Sprachvarianten') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">{{ status.active_translations_count ?? 0 }}</div>
                            </div>
                            <div class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('CMS CSS') }}</div>
                                <div class="min-w-0 text-sm text-gray-800">
                                    <a
                                        v-if="status.css_url"
                                        :href="status.css_url"
                                        class="text-sm text-gray-700 hover:text-gray-900 underline break-all"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {{ status.css_url }}
                                    </a>
                                    <span v-else>-</span>
                                </div>
                            </div>
                            <div v-if="status.last_build_message" class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Letzte Meldung') }}</div>
                                <div class="min-w-0 break-words text-sm text-gray-800">{{ status.last_build_message }}</div>
                            </div>
                            <div v-if="status.build_available === false && status.build_unavailable_reason" class="grid gap-2 px-5 py-4 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)] lg:gap-6">
                                <div class="text-xs font-semibold uppercase tracking-[0.08em] text-gray-500">{{ $t('Build-Verfügbarkeit') }}</div>
                                <div class="min-w-0 break-words text-sm text-amber-700">{{ status.build_unavailable_reason }}</div>
                            </div>
                        </div>
                    <template #footer>
                        <div class="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <label class="inline-flex items-center text-sm text-gray-700">
                                <input
                                    v-model="status.minify"
                                    class="form-checkbox rounded border-gray-300 text-gray-800 focus:ring-gray-300"
                                    type="checkbox"
                                    @change="saveMinifySetting"
                                >
                                <span class="ml-3">{{ $t('CSS komprimieren') }}</span>
                            </label>
                            <button class="btn-stage-primary disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="building || status.build_available === false" @click="buildAssets">
                                {{ $t('Tailwind CSS kompilieren') }}
                            </button>
                        </div>
                    </template>
                </app-panel>

                <app-panel
                    :title="$t('Globale CSS Variablen')"
                    panel-class="h-full xl:col-span-1 cms-config-variables-panel flex flex-col"
                    body-class="!p-0 cms-config-code-body"
                >
                    <html-code-editor v-model="variablesCss" :rows="14" variant="vault" fill />
                    <template #footer>
                        <div class="w-full flex items-center justify-end">
                            <button class="btn-stage-secondary" type="button" @click="saveVariablesCss">
                                {{ $t('Variablen speichern') }}
                            </button>
                        </div>
                    </template>
                </app-panel>
            </div>
        </div>
    </div>
</template>

<script>
import HtmlCodeEditor from "@/components/forms/html-code-editor";

export default {
    name: "cms-config",
    components: {
        HtmlCodeEditor,
    },
    data() {
        return {
            loading: true,
            building: false,
            variablesCss: '',
            status: {
                is_dirty: true,
                tailwind_version: null,
                last_changed_at: null,
                last_build_at: null,
                last_build_status: 'never',
                last_build_message: null,
                css_url: null,
                active_templates_count: 0,
                active_pages_count: 0,
                active_translations_count: 0,
                minify: false,
                variables_css: '',
                build_available: true,
                build_unavailable_reason: null,
            },
        };
    },
    computed: {
        statusLabel() {
            if (this.status.last_build_status === 'failed') {
                return this.$t('Fehler beim letzten Build');
            }

            if (this.status.is_dirty) {
                return this.$t('Neukompilierung erforderlich');
            }

            if (this.status.last_build_status === 'success') {
                return this.$t('Current').toString();
            }

            return this.$t('Noch nicht kompiliert');
        },
        statusClass() {
            if (this.status.last_build_status === 'failed') {
                return 'text-red-600';
            }

            if (this.status.is_dirty) {
                return 'text-amber-600';
            }

            if (this.status.last_build_status === 'success') {
                return 'text-green-600';
            }

            return 'text-gray-500';
        },
    },
    mounted() {
        this.loadStatus();
    },
    methods: {
        loadStatus() {
            this.loading = true;
            axios.get('api/workspace/admin/cms/assets/status').then((response) => {
                this.status = response.data || {};
                this.variablesCss = this.status.variables_css || '';
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        buildAssets() {
            if (this.building) {
                return;
            }

            this.building = true;
            axios.post('api/workspace/admin/cms/assets/build', {
                minify: !!this.status.minify,
            }).then((response) => {
                this.status = response.data.status || this.status;
                this.building = false;
                this.$notify({title: this.$t('Success'), text: this.$t(response.data.message || 'CMS CSS wurde erfolgreich kompiliert.'), type: 'success'});
            }).catch((error) => {
                this.status = error?.response?.data?.status || this.status;
                this.building = false;
                this.$notify({title: this.$t('Error'), text: error?.response?.data?.error || error?.response?.data?.message || this.$t('CMS CSS konnte nicht kompiliert werden.'), type: 'error'});
            });
        },
        saveMinifySetting() {
            axios.post('api/workspace/admin/cms/assets/settings', {
                minify: !!this.status.minify,
            }).then((response) => {
                this.status = response.data.status || this.status;
                this.variablesCss = this.status.variables_css || this.variablesCss;
                this.$notify({
                    title: this.$t('Success'),
                    text: this.$t(response.data.message || 'Die Build-Einstellung wurde gespeichert.'),
                    type: 'success'
                });
            }).catch(() => {
                this.loadStatus();
                this.$notify({title: this.$t('Error'), text: this.$t('Die Build-Einstellung konnte nicht gespeichert werden.'), type: 'error'});
            });
        },
        saveVariablesCss() {
            axios.post('api/workspace/admin/cms/assets/settings', {
                variables_css: this.variablesCss,
            }).then((response) => {
                this.status = response.data.status || this.status;
                this.variablesCss = this.status.variables_css || this.variablesCss;
                this.$notify({
                    title: this.$t('Success'),
                    text: this.$t(response.data.message || 'Die CSS-Variablen wurden gespeichert.'),
                    type: 'success'
                });
            }).catch(() => {
                this.loadStatus();
                this.$notify({title: this.$t('Error'), text: this.$t('Die CSS-Variablen konnten nicht gespeichert werden.'), type: 'error'});
            });
        },
        formatDateTime(value) {
            if (!value) {
                return '-';
            }

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return date.toLocaleString('de-DE');
        },
    },
}
</script>

<style scoped>
.cms-config-code-body {
    padding: 0 !important;
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.cms-config-variables-panel {
    display: flex;
    flex-direction: column;
}

.cms-config-variables-panel :deep(.stage-panel-body) {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
}

.cms-config-code-body :deep(.vault-html-editor) {
    flex: 1 1 auto;
    min-height: 100%;
    height: 100%;
}

.cms-config-code-body :deep(.vault-html-editor__gutter),
.cms-config-code-body :deep(.vault-html-editor__content),
.cms-config-code-body :deep(.vault-html-editor__highlight),
.cms-config-code-body :deep(.vault-html-editor__textarea) {
    height: 100%;
}
</style>
